// The request form (02-DESIGN/03-console/03-the-signed-out-site.md § the
// request form; hq decision 0076): where the install does not admit a
// stranger but names a mailto: address, *Request access* replaces the
// opening's words with this form, which posts the four fields to the page's
// own origin — the auth service's POST /auth/request-access — and shows the
// confirmation in its place once the platform accepted it. The page reads
// nothing back but the status; the sentences are its own.

import { useState, type SyntheticEvent } from "react";

import { Button, Field, Notice, TextArea, TextInput } from "../../ui/components.tsx";
import {
  BACK,
  CANCEL,
  OR_WRITE_TO,
  REQUEST_ACCESS,
  REQUEST_FIELDS,
  REQUEST_LEAD,
  REQUEST_REFUSED,
  REQUEST_SENDING,
  REQUEST_SENT,
  SEND_REQUEST,
} from "./words.ts";

/** The path the form posts to, at the page's own origin. */
export const REQUEST_ACCESS_PATH = "/auth/request-access";

/** MAX_MESSAGE is the platform's cut for the message (mail.MaxText). */
export const MAX_MESSAGE = 1000;

/** Refusal is the one sentence shown over the actions, by the status the platform answered. */
export type Refusal = "name" | "email" | "message" | "limit" | "unavailable";

/** refusalFor is the sentence for a status, with what the page can tell locally for a 400. */
export function refusalFor(
  status: number,
  fields: { name: string; email: string; message: string },
): Refusal {
  if (status === 429) {
    return "limit";
  }
  if (status === 400) {
    if (fields.name.trim() === "") {
      return "name";
    }
    if (fields.message.trim() === "") {
      return "message";
    }
    return "email";
  }
  return "unavailable";
}

/** looksLikeAnAddress is the page's own check before it posts: one @, something on each side, a dot in the domain. */
export function looksLikeAnAddress(s: string): boolean {
  const at = s.lastIndexOf("@");
  return at > 0 && at < s.length - 1 && s.slice(at + 1).includes(".") && !/[\s<>,;"]/.test(s);
}

/**
 * postRequest posts the form and answers the status; a network failure is
 * read as the platform unavailable.
 */
export async function postRequest(
  origin: string,
  body: { name: string; email: string; organisation: string; message: string; website: string },
  fetchFn: typeof fetch = fetch,
): Promise<number> {
  try {
    const res = await fetchFn(origin + REQUEST_ACCESS_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
    });
    return res.status;
  } catch {
    return 503;
  }
}

export function RequestAccessForm({
  origin,
  address,
  onClose,
}: {
  /** The page's own origin, which the form posts to. */
  origin: string;
  /** The address the profile names: the line under the form, and where the platform mails. */
  address: string;
  /** Cancel, and Back once sent. */
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | undefined>(undefined);
  const [refusal, setRefusal] = useState<Refusal | undefined>(undefined);

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (sending) {
      return;
    }
    if (name.trim() === "") {
      setRefusal("name");
      return;
    }
    if (!looksLikeAnAddress(email.trim())) {
      setRefusal("email");
      return;
    }
    if (message.trim() === "") {
      setRefusal("message");
      return;
    }
    setRefusal(undefined);
    setSending(true);
    void postRequest(origin, { name, email: email.trim(), organisation, message, website: "" }).then(
      (status) => {
        setSending(false);
        if (status === 202) {
          setSent(email.trim());
        } else {
          setRefusal(refusalFor(status, { name, email, message }));
        }
      },
    );
  };

  if (sent !== undefined) {
    return (
      <div className="site-request" role="status">
        <h2 className="site-request-title">{REQUEST_SENT.title}</h2>
        <p className="site-lede">{REQUEST_SENT.text(sent)}</p>
        <div className="site-actions">
          <Button kind="quiet" size="lg" onClick={onClose}>
            {BACK}
          </Button>
        </div>
      </div>
    );
  }

  const near = message.length >= MAX_MESSAGE - 100;
  return (
    <form className="site-request" onSubmit={submit} noValidate>
      <h2 className="site-request-title">{REQUEST_ACCESS}</h2>
      <p className="site-lede">{REQUEST_LEAD}</p>
      <div className="stack">
        <Field label={REQUEST_FIELDS.name}>
          <TextInput value={name} onChange={setName} required autoFocus disabled={sending} />
        </Field>
        <Field label={REQUEST_FIELDS.email}>
          <TextInput value={email} onChange={setEmail} required disabled={sending} />
        </Field>
        <Field label={REQUEST_FIELDS.organisation}>
          <TextInput value={organisation} onChange={setOrganisation} disabled={sending} />
        </Field>
        <Field label={REQUEST_FIELDS.message} hint={near ? `${message.length} / ${MAX_MESSAGE}` : undefined}>
          <TextArea
            value={message}
            onChange={(v) => {
              setMessage(v.length > MAX_MESSAGE ? v.slice(0, MAX_MESSAGE) : v);
            }}
            rows={5}
            disabled={sending}
          />
        </Field>
        {/* The honeypot: never shown, never filled by a person; the platform drops a request that fills it. */}
        <div className="site-request-trap" aria-hidden="true">
          <label>
            website
            <input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
          </label>
        </div>
      </div>
      {refusal !== undefined && (
        <div className="site-err">
          <Notice kind="bad">{REQUEST_REFUSED[refusal](address)}</Notice>
        </div>
      )}
      <div className="site-actions">
        <Button kind="primary" size="lg" type="submit" disabled={sending}>
          {sending ? REQUEST_SENDING : SEND_REQUEST}
        </Button>
        <Button kind="quiet" size="lg" onClick={onClose} disabled={sending}>
          {CANCEL}
        </Button>
      </div>
      <p className="site-under">
        {OR_WRITE_TO}{" "}
        <a className="site-link" href={`mailto:${address}`}>
          {address}
        </a>
      </p>
    </form>
  );
}
